import { useState } from 'react'
import Layout from './components/Layout'
import Dashboard from './components/Dashboard'
import TaskDecomposition from './components/TaskDecomposition'
import MedicationTracker from './components/MedicationTracker'
import ClassSchedule from './components/ClassSchedule'
import TermManager from './components/TermManager'
import Settings from './components/Settings'
import DailySchedules from './components/DailySchedules'
import FocusMode from './components/FocusMode'
import TimeSimulator from './components/TimeSimulator'
import AuthPage from './components/AuthPage'
import ErrorBoundary from './components/ErrorBoundary'
import ToastContainer from './components/ToastContainer'
import { DashboardSkeleton } from './components/Skeleton'
import { useAuth } from './hooks/useAuth'
import { useSupabaseData } from './hooks/useSupabaseData'
import { TimeProvider } from './contexts/TimeContext'
import { NotificationProvider } from './contexts/NotificationContext'
import './styles/index.css'

function App() {
  const { user, loading: authLoading, passwordRecovery, signUp, signIn, signOut, resetPassword, updatePassword, clearPasswordRecovery } = useAuth()

  if (authLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
      </div>
    )
  }

  if (!user) {
    return <AuthPage onSignIn={signIn} onSignUp={signUp} onResetPassword={resetPassword} />
  }

  if (passwordRecovery) {
    return <AuthPage passwordRecovery onUpdatePassword={updatePassword} onPasswordUpdateComplete={clearPasswordRecovery} />
  }

  return (
    <TimeProvider>
      <ErrorBoundary>
        <AuthenticatedApp user={user} signOut={signOut} />
      </ErrorBoundary>
      {import.meta.env.DEV && <TimeSimulator />}
    </TimeProvider>
  )
}

function AuthenticatedApp({ user, signOut }) {
  const [currentPage, setCurrentPage] = useState('dashboard')
  const [expandTaskId, setExpandTaskId] = useState(null)
  const [focusTarget, setFocusTarget] = useState(null)

  const {
    terms, activeTermId, tasks, medications, medLog, classes, dataLoading,
    addTerm, updateTerm, deleteTerm, switchTerm,
    setTasks, toggleSubtask, deleteTask, updateTask, updateSubtask, addSubtask, deleteSubtask, reorderSubtasks, replaceSubtasks,
    addMedication, updateMedication, deleteMedication, toggleMedLog,
    addClass, updateClass, deleteClass,
    displayName, updateDisplayName,
    defaultDayStart, defaultDayEnd, updateDayDefaults,
    savedSchedule, saveSchedule, loadScheduleByDate, listScheduleDates,
    refresh,
  } = useSupabaseData(user.id)

  function navigateTo(page, taskId) {
    setCurrentPage(page)
    setExpandTaskId(taskId || null)
  }

  const pageProps = {
    dashboard: { terms, tasks, classes, medications, medLog, onToggleMedLog: toggleMedLog, onToggleSubtask: toggleSubtask, onNavigate: navigateTo, displayName, defaultDayStart, defaultDayEnd, savedSchedule, onSaveSchedule: saveSchedule, loadScheduleByDate },
    tasks: {
      tasks, setTasks,
      expandTaskId,
      onClearExpandTask: () => setExpandTaskId(null),
      onToggleSubtask: toggleSubtask,
      onDeleteTask: deleteTask,
      onUpdateTask: updateTask,
      onUpdateSubtask: updateSubtask,
      onAddSubtask: addSubtask,
      onDeleteSubtask: deleteSubtask,
      onReorderSubtasks: reorderSubtasks,
      onReplaceSubtasks: replaceSubtasks,
      onStartFocus: setFocusTarget,
    },
    schedule: {
      classes,
      onAddClass: addClass,
      onUpdateClass: updateClass,
      onDeleteClass: deleteClass,
    },
    medication: {
      medications, medLog,
      onAddMedication: addMedication,
      onUpdateMedication: updateMedication,
      onDeleteMedication: deleteMedication,
      onToggleMedLog: toggleMedLog,
    },
    terms: {
      terms,
      activeTermId,
      onAddTerm: addTerm,
      onUpdateTerm: updateTerm,
      onDeleteTerm: deleteTerm,
      onSwitchTerm: switchTerm,
    },
    settings: {
      displayName,
      onUpdateDisplayName: updateDisplayName,
      defaultDayStart,
      defaultDayEnd,
      onUpdateDayDefaults: updateDayDefaults,
    },
    'daily-schedules': {
      loadScheduleByDate,
      listScheduleDates,
    },
  }

  const pages = {
    dashboard: Dashboard,
    schedule: ClassSchedule,
    tasks: TaskDecomposition,
    medication: MedicationTracker,
    terms: TermManager,
    settings: Settings,
    'daily-schedules': DailySchedules,
  }

  const Page = pages[currentPage] ?? Dashboard
  const props = pageProps[currentPage] ?? {}

  if (dataLoading) {
    return (
      <Layout currentPage={currentPage} onNavigate={setCurrentPage} terms={terms} activeTermId={activeTermId} onSwitchTerm={switchTerm} onSignOut={signOut} onRefresh={refresh}>
        <DashboardSkeleton />
      </Layout>
    )
  }

  return (
    <NotificationProvider schedule={savedSchedule} tasks={tasks} medications={medications}>
      <Layout currentPage={currentPage} onNavigate={setCurrentPage} terms={terms} activeTermId={activeTermId} onSwitchTerm={switchTerm} onSignOut={signOut} onRefresh={refresh}>
        <Page {...props} />
      </Layout>
      <ToastContainer />
      {focusTarget && (
        <FocusMode
          task={tasks.find((t) => t.id === focusTarget.taskId)}
          startSubtaskId={focusTarget.subtaskId}
          onToggleSubtask={toggleSubtask}
          onClose={() => setFocusTarget(null)}
        />
      )}
    </NotificationProvider>
  )
}

export default App
